// Implementation manuelle PKCE (RFC 7636) pour OAuth2 Authorization Code Flow + Cognito.
// Zero dependance, audit-friendly. Cf .claude/rules/frontend-security.md pour le rationnel.

const PKCE_VERIFIER_KEY = 'rmf:auth:pkce_verifier';
const PKCE_STATE_KEY = 'rmf:auth:pkce_state';
const PKCE_RETURN_TO_KEY = 'rmf:auth:pkce_return_to';

/** Genere un code_verifier RFC 7636 : 32 octets random encodes base64url -> 43 chars URL-safe. */
export function generateCodeVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/** code_challenge = base64url(SHA256(verifier)). C'est ce qui part avec /authorize. */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(hash));
}

/** State opaque : protection CSRF du callback. Compare au retour pour rejeter un callback tiers. */
export function generateState(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/** Base64 URL-safe sans padding (RFC 4648 § 5). btoa standard ne le fait pas, on adapte. */
function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// --- Stockage des artefacts du flow entre /authorize et /callback ---
// Le verifier ne doit JAMAIS quitter l'origine (sinon protection PKCE perdue). sessionStorage
// est l'endroit canonique : isole par origine, propre au tab, perdu si tab ferme avant retour.

export type PkceArtifacts = {
  codeVerifier: string;
  state: string;
  returnTo: string;
};

export function storeArtifacts(artifacts: PkceArtifacts): void {
  sessionStorage.setItem(PKCE_VERIFIER_KEY, artifacts.codeVerifier);
  sessionStorage.setItem(PKCE_STATE_KEY, artifacts.state);
  sessionStorage.setItem(PKCE_RETURN_TO_KEY, artifacts.returnTo);
}

/** Lit les artefacts ET les efface : un code_verifier ne sert qu'une fois (anti-replay). */
export function consumeArtifacts(): PkceArtifacts | null {
  const codeVerifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  const state = sessionStorage.getItem(PKCE_STATE_KEY);
  const returnTo = sessionStorage.getItem(PKCE_RETURN_TO_KEY);
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(PKCE_STATE_KEY);
  sessionStorage.removeItem(PKCE_RETURN_TO_KEY);
  if (!codeVerifier || !state) return null;
  return { codeVerifier, state, returnTo: returnTo ?? '/office' };
}
